'use memo'

// web/src/pages/Event/index.tsx
import { useState } from 'react'
import { useHistory, useParams } from 'react-router-dom'
import { message } from 'antd'
import { ROUTES } from '@sportninja/common/constants/app'
import { capitalizeWords } from '@sportninja/common/ts-utils'
import appOrigin from '@sportninja/common/constants/appOrigin'
import PageUntyped from 'src/components/Page'
import ShareModal from 'src/components/ShareModal'
import ErrorScreen from 'src/components/ErrorScreen'
import Helmet from 'src/components/Helmet'
import { useEvent } from '@sportninja/common/features/event/hooks/useEvent'
import { useEventModal } from '@sportninja/common/features/event/hooks/useEventModal'
import { useEventMutations } from '@sportninja/common/features/event/hooks/useEventMutations'
import { formatEventDateRange } from '@sportninja/common/features/event/formatEventDateRange'
import {
  EVENT_FORBIDDEN_MESSAGE,
  resolveEventError,
} from '@sportninja/common/features/event/resolveEventError'
import EventDetailBody from 'src/features/event/components/EventDetailBody'
import EventModal from 'src/features/event/components/EventModal'
import EventDetailActions from 'src/features/event/components/EventDetailActions'
import DeleteEventModal from 'src/features/event/components/DeleteEventModal'
import {
  isSeriesEvent,
  SERIES_SCOPE_DEFAULT,
  type EventSeriesScope,
} from '@sportninja/common/features/event/seriesScope'
import EventDetailSkeleton from 'src/features/event/components/EventDetailSkeleton'
import { useEventPermission } from '@sportninja/common/features/event/hooks/useEventPermission'
import { buildEventBreadcrumbs } from '@sportninja/common/features/event/buildEventBreadcrumbs'
import BetaBadge from 'src/components/ts-components/BetaBadge'

// `Page` is a plain .jsx component (no prop types); TS infers an overly-narrow
// `null`-only type for the optional `subTitle` string prop from its JS
// default value. Widen locally rather than edit the shared component used by
// every entity page.
interface PageProps {
  loading?: boolean
  error?: Error | { status: number; message: string } | null
  title?: string
  subTitle?: string | null
  baseRoute?: string
  baseCrumb?: string
  baseTitle?: string
  iconName?: string
  isEvent?: boolean
  pages?: unknown[]
  titleActions?: React.ReactNode[]
  /** Top-align `titleActions` instead of baseline-aligning them. */
  titleActionsAlignTop?: boolean
  /** Right-hand header slot, opposite the title. */
  headerActions?: React.ReactNode
  breadcrumbs?: Array<{ id: string; to: string; text: string }>
  /** Use each crumb's own `to` instead of the default /org/{id}. */
  useCrumbPaths?: boolean
}
const Page = PageUntyped as unknown as React.FC<PageProps>

/** Shape `Page` knows how to render an error screen for — see
 *  web/src/components/Page/index.jsx (only `.status === 403 / 404` render
 *  anything; everything else silently falls through to a blank header). */
interface PageError {
  status: number
  message: string
}

/** Thin gate for /event/:id — resolves the event and delegates rendering. */
const Event: React.FC = () => {
  const { id } = useParams<{ id: string }>()
  const history = useHistory()
  const {
    data: event,
    permission: eventPermission,
    isLoading,
    error: queryError,
  } = useEvent(id)
  const eventModal = useEventModal()
  const [isDeleteOpen, setIsDeleteOpen] = useState(false)
  const [isShareOpen, setIsShareOpen] = useState(false)
  // Defaults to the least destructive choice, so a mis-click on Delete cannot
  // wipe a whole series.
  const [deleteScope, setDeleteScope] =
    useState<EventSeriesScope>(SERIES_SCOPE_DEFAULT)

  // Both failure paths (a thrown request error, and a settled event-less
  // response for a DELETED event) are normalized in one shared, tested helper
  // — see common/features/event/resolveEventError.
  const pageError: PageError | null = resolveEventError({
    error: queryError,
    event,
    isLoading,
  })

  // Derived from the LOADED event only — while it's still `null`/undefined
  // (loading, or the event doesn't exist) teamId falls back to an inert
  // placeholder that must never be used to fire a real mutation. The hook is
  // still called unconditionally, for hook-order safety; invalidation is only
  // correct once `event` has resolved, which is the only time these mutations
  // can actually be triggered from this page (edit/delete require `event`).
  const teamId = event?.team?.id ?? ''
  const { removeEvent, isDeleting } = useEventMutations({ teamId })

  const isSeries = isSeriesEvent(event)

  // Derived from the event payload's own `team` — no request.
  const breadcrumbs = buildEventBreadcrumbs({
    event,
    orgRoot: ROUTES.ORG_ROOT,
    scheduleRoot: ROUTES.SCHEDULE_ROOT,
    teamRoot: ROUTES.TEAM_ROOT,
  })

  // Events carry no permissions of their own — the API checks the parent team,
  // and a plain player who can VIEW an event gets a 403 on edit/delete. Both
  // controls are hidden unless the user can manage the team. The answer rides
  // along on the event response itself, so this costs no extra request.
  const { canView, canEdit, canDelete } = useEventPermission({
    eventPermission,
  })

  // A fetch failure outranks the permission gate: a 403 or 404 from the API is
  // the more specific answer, and `canView` is only meaningful once an event
  // has actually come back. Reversed, an event that failed to load would be
  // reported as forbidden regardless of why it failed.
  // Rendered by this page rather than handed to `Page` as a 403: that branch
  // is shared with every private entity and hardcodes its own "X is private"
  // copy plus a Contact Support button, discarding the message it is given.
  // This case wants its own wording and ErrorScreen's default GO HOME.
  const isForbidden = Boolean(event) && !canView

  // The team's SCHEDULE — where the event was listed. There is no `/events`
  // tab (that was cut); pointing there produced a dead route after delete.
  const parentRoute = event?.team
    ? `${ROUTES.TEAM_ROOT}/${event.team.id}/schedule`
    : ROUTES.HOME

  const handleDelete = async () => {
    if (!event) {
      return
    }
    try {
      // Scope only means something for a repeating event; a one-off sends
      // none so the request is unchanged from before.
      await removeEvent(event.id, isSeries ? deleteScope : undefined)
      // No success toast — navigating away from the deleted event's page is
      // itself the confirmation.
      setIsDeleteOpen(false)
      history.push(parentRoute)
    } catch (err) {
      message.error((err as Error).message)
    }
  }

  // After every hook, so hook order is unaffected by taking this branch.
  // Replaces the whole page: the event object exists (the request succeeded),
  // so anything rendered below would leak the content this gate withholds.
  if (isForbidden) {
    return (
      <>
        <Helmet title='Access Denied' />
        <ErrorScreen title='Access Denied' message={EVENT_FORBIDDEN_MESSAGE} />
      </>
    )
  }

  return (
    <>
      <Page
        loading={isLoading}
        error={pageError}
        title={event ? capitalizeWords(event.name) : ''}
        // Renders INLINE beside the title text (unlike `headerActions`, which
        // sits opposite it on the right).
        titleActions={[<BetaBadge key='beta' />]}
        // The BETA pill is short; baseline-aligning it against the very large
        // page title reads as hanging off the bottom.
        titleActionsAlignTop
        subTitle={
          event ? formatEventDateRange(event.starts_at, event.ends_at) : null
        }
        baseRoute={`${ROUTES.EVENT_ROOT}/${id}`}
        baseCrumb={parentRoute}
        baseTitle={event?.team?.name ?? event?.team?.name_full}
        iconName='calendar-alt'
        // Swaps the header's default competition-trophy placeholder for a
        // calendar mark; events carry no image of their own.
        isEvent
        // The event page has no tabs. An empty array would still render a 38px
        // bordered tab strip; PageMenu only collapses to 0px at length === 1,
        // and skips pages marked `hidden`. So one hidden entry gives a
        // tab-less header without touching the shared component. The body is
        // rendered as a sibling below, so this page needs no component.
        pages={[{ to: `${ROUTES.EVENT_ROOT}/${id}`, text: '', hidden: true }]}
        // The parent team, above the title — from the event payload itself.
        breadcrumbs={breadcrumbs}
        // This trail mixes an org, a competition and a team, each with its own
        // route — the default /org/{id} rebuild would send all three to org.
        useCrumbPaths
        // RIGHT-hand header slot, opposite the title — NOT `titleActions`,
        // which renders inline beside the title text. The component renders
        // nothing when the user has neither capability.
        headerActions={
          event ? (
            <EventDetailActions
              canEdit={canEdit}
              canDelete={canDelete}
              onEdit={() => eventModal.openEdit(event)}
              onDelete={() => setIsDeleteOpen(true)}
              onShare={() => setIsShareOpen(true)}
            />
          ) : null
        }
      />

      {/* Skeleton only while the fetch is in flight and nothing has failed —
          on error `Page` renders its own error screen, and a skeleton beneath
          it would suggest content is still coming. */}
      {isLoading && !pageError && <EventDetailSkeleton />}

      {event && !isForbidden && <EventDetailBody event={event} />}

      {/* Also gated: the buttons are already hidden, but mounting the modals
          for a user who cannot act keeps a path open to a guaranteed 403. */}
      {event && canEdit && (
        <EventModal
          isOpen={eventModal.isOpen}
          onClose={eventModal.close}
          event={eventModal.editingEvent}
          teamId={teamId}
          // Enables the Location/Facility pickers for a COMPETITION event, so
          // editing one here gets the same modal the schedule page shows
          // rather than falling back to the address fields.
          //
          // `team` is the discriminator, NOT the presence of a schedule: a
          // TEAM event carries `schedule: null` but still reports the org's
          // `root_schedule`, so keying off that alone wrongly gave team events
          // the pickers on edit.
          //
          // Scoped to `root_schedule` (the top-level competition) rather than
          // `schedule` (which may be a division), since
          // `/venues?schedule_id=` is scoped to the competition owning the
          // venue pool.
          scheduleId={
            event.team
              ? undefined
              : event.root_schedule?.id ?? event.schedule?.id ?? undefined
          }
        />
      )}

      {/* No permission gate, unlike the edit/delete modals: sharing a link the
          viewer already has open needs no rights of its own. */}
      {event && (
        <ShareModal
          isOpen={isShareOpen}
          onClose={() => setIsShareOpen(false)}
          entityType='event'
          entityName={event.name ?? 'Event'}
          shareUrl={`${appOrigin}${ROUTES.EVENT_ROOT}/${event.id}`}
        />
      )}

      {canDelete && (
        <DeleteEventModal
          isOpen={isDeleteOpen}
          eventName={event?.name ?? ''}
          isLoading={isDeleting}
          isSeries={isSeries}
          scope={deleteScope}
          onScopeChange={setDeleteScope}
          onClose={() => setIsDeleteOpen(false)}
          onConfirm={handleDelete}
        />
      )}
    </>
  )
}

export default Event
